import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type { CreateNamespaceInput } from '@erp/rpc';

/**
 * Hooks over the org's namespaces (`namespaces.*`). The list read is shared
 * by the inbox filter, workflow/agent assignment pickers, and the task type
 * admin's default namespace picker.
 */

export function invalidateNamespaceQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: orpc.namespaces.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.namespaces.getContents.key(),
  });
}

export function useNamespaces(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.namespaces.list.queryOptions({ input: {} }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Inventory of one namespace: task count, task types, workflows, agents. */
export function useNamespaceContents(
  namespaceId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...orpc.namespaces.getContents.queryOptions({
      input: { id: namespaceId },
    }),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateNamespace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNamespaceInput) =>
      rpcClient.namespaces.create(input),
    onSuccess: () => {
      invalidateNamespaceQueries(queryClient);
    },
  });
}
